import * as analytic from '@anm/analytic';
import { UploadFileProps } from '@anm/components/InputFile';
import dataURItoBlob from '@anm/helpers/dataURItoBlob';
import getExtensionFromPath from '@anm/helpers/getExtensionFromPath';
import getFrameFromVideo from '@anm/helpers/getFrameFromVideo';
import isStreamVideo from '@anm/helpers/is/isStreamVideo';
import useSwitchState from '@anm/hooks/useSwitchState';
import useVideoStatusChecker from '@anm/hooks/useVideoStatusChecker';
import { Poster } from '@anm/hosting-player';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import vtConfig from '../../../config';
import { hostedVideoActions, hostedVideoSelectors } from '../../../store/modules/hostedVideo';
import useNotificationPosition from '../CustomizationSection/hooks/useNotificationPosition';

type UsePosterBlockProps = {
  poster: Poster;
  videoUrl: string;
  currentTime?: number;
  posterHeight: number;
};

type UpdatePosterByCurrentFrameProps = {
  file?: File;
  streamVideo?: HTMLVideoElement;
};

export const usePosterBlock = ({ videoUrl, poster, currentTime = 0, posterHeight }: UsePosterBlockProps) => {
  const [customUploadStatus, setCustomUploadStatus] = useState(false);
  const [uploadFrameStatus, setUploadFrameStatus] = useState(false);
  const dispatch = useDispatch();

  const updatePoster = (poster: Poster) => dispatch(hostedVideoActions.updatePoster(poster));

  const uploadCustomPoster = async (props: UploadFileProps[]) => {
    const { file, objectUrl } = props[0];
    setCustomUploadStatus(true);
    analytic.trackVideoUploadThumbnail();

    const extension = getExtensionFromPath(file.name);
    updatePoster({ file, extension, posterUrl: objectUrl });
    setCustomUploadStatus(false);
  };

  const updatePosterByCurrentFrame = async ({ file, streamVideo }: UpdatePosterByCurrentFrameProps) => {
    const frame = (await getFrameFromVideo({
      file,
      streamVideo,
      frameMaxHeight: posterHeight,
      frameSecond: currentTime
    })) as string;

    const posterBlob = dataURItoBlob(frame);
    const posterFile = new File([posterBlob], 'poster');

    updatePoster({
      ...poster,
      ...{ file: posterFile, posterUrl: frame }
    });
    setUploadFrameStatus(false);
  };

  const setPosterByFrame = () => {
    setUploadFrameStatus(true);
    analytic.trackVideoUseCurrentFrame();

    (async () => {
      const isStream = isStreamVideo(videoUrl);

      if (isStream) {
        updatePosterByCurrentFrame({ streamVideo: window.hls.media });
        return;
      }

      await fetch(videoUrl)
        .then(resp => resp.blob())
        .then(async blob => {
          const file = new File([blob], 'video');
          updatePosterByCurrentFrame({ file });
        })
        .catch(e => console.error(e));
    })();
  };

  return [uploadCustomPoster, setPosterByFrame, uploadFrameStatus, customUploadStatus] as const;
};

export const useCheckPoster = (hostId: string) => {
  const dispatch = useDispatch();
  const isPosterPending = useSelector(hostedVideoSelectors.selectSavePosterPending);

  const [isPosterReady, setIsPosterReady] = useState(false);
  const [prevPosterPending, setPrevPosterPending] = useState(false);

  const canCheckPoster = prevPosterPending && !isPosterPending && !isPosterReady;

  const { isMediaReady } = useVideoStatusChecker({
    hostId,
    source: 'api',
    mediaType: 'image',
    canCleanState: isPosterReady,
    progress: canCheckPoster ? 100 : null,
    supportedExtensions: vtConfig().supportedImageExtensions
  });

  useEffect(() => {
    if (isPosterPending === prevPosterPending || prevPosterPending) return;

    setPrevPosterPending(true);
  }, [isPosterPending]);

  useEffect(() => {
    if (!(isMediaReady && !isPosterReady)) return;

    dispatch(hostedVideoActions.triggerPosterStatus());
    setIsPosterReady(true);
  }, [isMediaReady]);

  useEffect(() => {
    if (!isPosterReady) return;

    setIsPosterReady(false);
    setPrevPosterPending(false);
  }, [isPosterReady]);

  const isChecking = prevPosterPending && !isPosterReady;

  return [isChecking, isPosterReady] as const;
};

export const useSuccessNotification = () => {
  const [isOpenNotification, openNotification, closeNotification] = useSwitchState();

  const dispatch = useDispatch();

  const posterStatus = useSelector(hostedVideoSelectors.selectPosterStatus);

  useEffect(() => {
    if (!posterStatus) return;

    openNotification();
  }, [posterStatus]);

  const [notificationPosition] = useNotificationPosition();

  const handleCloseNotification = useCallback(() => {
    closeNotification();
    dispatch(hostedVideoActions.triggerPosterStatus());
  }, []);

  return [isOpenNotification, handleCloseNotification, notificationPosition] as const;
};
